import { generateClient } from "aws-amplify/api";
import { updateFieldInstance, updateFormInstance, updateFormGroup } from "../graphql/mutations";
import { recordSentry } from "./recordSentry";
import { FieldInstanceForResolver } from "db/definitions";

/**
 * updates a solo field instance.
 * @param updatedInstance to update
 * @returns true when the field instance itself was written, false when that write failed. The failure is also reported
 * to Sentry here, so callers only need the boolean to decide what to tell the user. The follow-up formInstance/formGroup
 * timestamp bumps are best-effort and do not affect the result.
 */
async function updateSingleFieldInstance(
	updatedInstance: Pick<FieldInstanceForResolver, "id" | "formGroup" | "group"> & Partial<FieldInstanceForResolver>,
): Promise<boolean> {
	const client = generateClient({ authMode: "userPool" });
	let fieldWritten = false;
	try {
		await client.graphql({
			query: updateFieldInstance,
			variables: {
				input: updatedInstance,
			},
			authMode: "userPool",
		});
		fieldWritten = true;
	} catch (error) {
		recordSentry({
			error,
			msg: "error updating single field instance:",
			additionalData: { fieldInstanceId: updatedInstance.id, formGroup: updatedInstance.formGroup },
		});
	}

	// Always bump formInstance/formGroup updatedAt so real-time subscriptions fire
	// for collaborators, even when the field write above returns a partial AppSync error.
	try {
		const timestamp = updatedInstance.updatedAt || new Date().toISOString();
		if (updatedInstance.formInstance) {
			await client.graphql({
				query: updateFormInstance,
				variables: {
					input: {
						id: updatedInstance.formInstance,
						updatedAt: timestamp,
					},
				},
				authMode: "userPool",
			});
		}
		await client.graphql({
			query: updateFormGroup,
			variables: {
				input: {
					id: updatedInstance.formGroup,
					group: updatedInstance.group,
					updatedAt: timestamp,
					lastUpdatedBy: updatedInstance.lastAuthor || updatedInstance.editing || "",
				},
			},
			authMode: "userPool",
		});
	} catch (error) {
		recordSentry({ error, msg: "error bumping formInstance/formGroup updatedAt after field instance update:" });
	}

	return fieldWritten;
}

export default updateSingleFieldInstance;
